<template>
  <v-dialog
    v-model="show"
    max-width="400px"
    class="rounded-lg"
    transition="scroll-y-reverse-transition"
    persistent
  >
    <v-card class="pa-0" min-height="200">
      <transition
        name="fade"
        mode="out-in"
        @before-leave="beforeLeave"
        @enter="enter"
        @after-enter="afterEnter"
      >
        <v-row class="ma-0" no-gutters>
          <v-col>
            <h2 class="text-center px-4 py-6">
              {{ $translate('ORDER_CONFIRMATION') }}
            </h2>

            <section v-if="!confirmed && hasMultiPaymentMethods">
              <h3 class="px-4 mb-3 text-center">
                {{ $translate('SELECT_PAYMENT_METHOD') }}
              </h3>
              <!-- Payment method selection -->
              <v-card flat>
                <PaymentMethodSelect
                  v-model="selectedPaymentMethod"
                  :items="paymentMethods"
                  :cols="4"
                  :md="4"
                  :ref="paymentMethodSelectElId"
                  :id="paymentMethodSelectElId"
                />
              </v-card>

              <v-row justify="center" class="ma-0 pa-3">
                <v-btn color="primary" @click="onConfirm">
                  {{ $translate('CONFIRM') }}
                </v-btn>
              </v-row>
            </section>

            <v-alert
              v-if="showAlert"
              text
              :type="alertType"
              class="alert-section"
            >
              {{ alertText }}
            </v-alert>
          </v-col>
        </v-row>
      </transition>

      <v-overlay :class="loading ? '' : 'd-none'" color="grey" absolute>
        <v-progress-circular indeterminate color="primary" />
      </v-overlay>
    </v-card>
  </v-dialog>
</template>

<script>
import uiHelpers from '@/shared/ui_helpers';
import doRequest from '@/api/dssRequest';
import PaymentMethodJS from '@/objects/PaymentMethod';
import PaymentMethodSelect from '@/components/booking/PaymentMethodSelect';

export default {
  name: 'Account',
  mixins: [uiHelpers],
  components: {
    PaymentMethodSelect,
  },
  data() {
    return {
      show: false,
      email: '',
      orderId: '',
      action: '',

      alertType: '',
      alertText: '',
      loading: false,
      prevHeight: 0,
      redirectTimeout: 10000, // 10 seconds
      paymentMethods: [],
      selectedPaymentMethod: null,
      paymentMethodSelectElId: 'paymentMethodSelect',
      confirmed: false,
      showAlert: false,
    };
  },
  computed: {
    userLoggedIn() {
      return this.$store.getters['auth/isValid'];
    },
    requestObjId() {
      if (this.userLoggedIn) {
        return this.$store.getters['auth/userName'];
      }
      return `${this.$store.getters['Account/partnerGroupDetails'].AccountId}_UM`;
    },
    hasMultiPaymentMethods() {
      return this.paymentMethods != null && this.paymentMethods.length > 1;
    },
  },
  created() {
    window.EventBus.listen(
      'triggerOpenUserConfirmation',
      this.onUserConfirmation
    );
  },
  beforeDestroy() {
    window.EventBus.removeListener('triggerOpenUserConfirmation');
  },
  methods: {
    async onUserConfirmation(args) {
      this.initArgs(args);
      this.clearPaymentMethods();

      if (this.action !== 'accept') {
        this.processOrder();
        return;
      }

      // Accept order
      try {
        this.loading = true;
        const response = await PaymentMethodJS.getPaymentMethodsForOrder(
          this.orderId
        );

        this.loading = false;

        if (response.PaymentUrl != null && response.PaymentUrl.trim() !== '') {
          // PaymentUrl available, do directly to the payment page
          this.onOrderWaitingForPayment(response.PaymentUrl);
          return;
        }

        // No PaymentUrl available, parse PaymentMethods
        this.paymentMethods = response.PaymentMethods;

        if (this.paymentMethods.length === 1) {
          [this.selectedPaymentMethod] = this.paymentMethods;
          this.processOrder();
        }
      } catch (error) {
        this.loading = false;
        this.alertText = this.$translate('GET_PAYMENT_METHODS_ERROR');
        this.alertType = 'error';
        this.showAlert = true;
        this.goHome();
      }
    },
    processOrder() {
      // Triggers the user confirmation dialog to be opened or closed
      this.confirmed = true;
      this.loading = true;

      const payload = {
        OrderId: this.orderId,
        Email: this.email,
        PaymentMethod: this.selectedPaymentMethod,
      };

      let functionName = '';

      if (this.userLoggedIn) {
        if (this.action === 'accept') functionName = 'confirmOrder';
        else if (this.action === 'reject') functionName = 'cancelOrder';
        else return;
      } else {
        if (this.action === 'accept') functionName = 'confirmNoLogin';
        else if (this.action === 'reject') functionName = 'cancelNoLogin';
        else return;
      }

      doRequest(
        (response) => {
          setTimeout(() => {
            this.loading = false;
          }, 100);

          this.onOrderProcessed(response);
        },
        () => {
          setTimeout(() => {
            this.loading = false;
          }, 100);
          this.alertType = 'error';
          this.alertText = this.$translate('ALERT_TEXT_ERROR');
          this.showAlert = true;
          this.goHome();
        },
        functionName,
        this.requestObjId,
        payload,
        'POST',
        600000,
        false
      );
    },
    beforeLeave(element) {
      this.prevHeight = getComputedStyle(element).height;
    },
    enter(element) {
      const { height } = getComputedStyle(element);

      element.style.height = this.prevHeight;

      setTimeout(() => {
        element.style.height = height;
      });
    },
    afterEnter(element) {
      element.style.height = 'auto';
    },
    onOrderProcessed(response) {
      switch (response.data.Status) {
        case 'Confirmed':
          this.onOrderConfirmed();
          break;
        case 'WaitingForPayment':
          this.onOrderWaitingForPayment(response.data.PaymentUrl);
          break;
        case 'Cancelled':
        case 'CancelledByUser':
          this.onOrderCancelled();
          break;
        case 'Done':
          this.onOrderDone();
          break;
        case 'PartnerConfirmation':
          this.onOrderPartnerConfirmation();
          break;
        default:
          this.alertType = 'success';
          this.alertText = '';
          this.showAlert = true;
          this.goHome();
          break;
      }
    },
    onOrderConfirmed() {
      this.alertType = 'success';
      this.alertText = this.$translate('ALERT_TEXT_CONFIRMED');
      this.showAlert = true;
      this.goHome();
    },
    onOrderWaitingForPayment(paymentUrl) {
      this.alertType = 'success';
      if (this.action === 'accept') {
        this.alertText = this.$translate('ALERT_TEXT_WAI_FOR_PAY_ACCEPT');
        this.showAlert = true;
        setTimeout(() => {
          this.show = false;
          window.location = paymentUrl;
        }, this.redirectTimeout);
      } else if (this.action === 'reject') {
        this.alertText = this.$translate('ALERT_TEXT_WAI_FOR_PAY_REJECT');
        this.showAlert = true;
        this.goHome();
      }
    },
    onOrderCancelled() {
      this.alertType = 'warning';
      this.alertText = this.$translate('ALERT_TEXT_CANCELLED');
      this.showAlert = true;
      this.goHome();
    },
    onOrderDone() {
      this.alertType = 'success';
      this.alertText = this.$translate('ALERT_TEXT_DONE');
      this.showAlert = true;
      this.goHome();
    },
    onOrderPartnerConfirmation() {
      this.alertType = 'info';
      this.alertText = this.$translate('ALERT_TEXT_PAR_CONF');
      this.showAlert = true;
      this.goHome();
    },
    goHome() {
      setTimeout(() => {
        this.show = false;
        this.$router.push({
          name: 'Homepage',
          params: {
            account_id: this.$store.getters.getRegion,
          },
        });
      }, this.redirectTimeout);
    },
    clearPaymentMethods() {
      this.paymentMethods = [];
      this.selectedPaymentMethod = null;
    },
    initArgs(args) {
      this.show = args.toggle;
      this.email = args.email;
      this.orderId = args.orderId;
      this.action = args.action;
    },
    clearArgs() {
      this.show = false;
      this.email = '';
      this.orderId = '';
      this.action = '';
    },
    clear() {
      this.clearArgs();
      this.clearPaymentMethods();
      this.confirmed = false;
      this.showAlert = false;
    },
    validatePaymentMethod() {
      const paymentRef = this.$refs[this.paymentMethodSelectElId];
      if (paymentRef != null && !paymentRef.validate()) {
        return false;
      }

      return true;
    },
    onConfirm() {
      if (!this.validatePaymentMethod()) {
        window.EventBus.fire('activateSnackBar', {
          text: this.$translate('LINDA_FILL_REQUIRED'),
          color: 'warning',
        });
        return;
      }

      this.processOrder();
    },
    onClose() {
      this.goHome();
      this.clear();
    },
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.2s;
  transition-property: height, opacity;
  transition-timing-function: ease;
  overflow: hidden;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}
.alert-section {
  position: absolute;
  bottom: 0px;
}
</style>
